import { ComboboxItem, Group, NumberInput, SimpleGrid, Stack, Title } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnnualReportButton } from '@/module/AnnualReport/components/AnnualReportButton';
import { IsiOverviewBox } from '@/module/AnnualReport/components/IsiOverviewBox';
import { ModelReportButton } from '@/module/AnnualReport/components/ModelReportButton';
import { ReportCountBox } from '@/module/AnnualReport/components/ReportCountBox';
import { getAnnualReportOptions } from '@/module/AnnualReport/model/AnnualReportQuery';

import { NoContentPlaceholder } from '@/core/components';
import { CustomSelect } from '@/core/components/Select';

import { useCurrentUser } from '@feature/security';
import { ValidationStatus } from '@feature/validation';

const startYear = new Date().getFullYear() - 1;

const validationOptions = [
  { label: 'Validat', value: 'VALIDATED' },
  { label: 'Respins', value: 'REJECTED' },
];

export function AnnualReportAnalytics() {
  const [yearStr, setYearStr] = useState<number | string>(String(startYear));
  const year = yearStr != null && yearStr !== '' ? Number(yearStr) : startYear;

  const { currentRole } = useCurrentUser();
  const [validationStatus, setValidationStatus] = useState<ValidationStatus | null>(
    currentRole === 'UNIVERSITY_ADMIN' || currentRole === 'FACULTY_ADMIN' ? 'VALIDATED' : null,
  );

  const { t } = useTranslation();

  const {
    data: { medicalFaculties, techFaculties },
  } = useSuspenseQuery(getAnnualReportOptions(year, null, validationStatus));
  const faculties = useMemo(() => [...medicalFaculties, ...techFaculties], [medicalFaculties, techFaculties]);
  const [faculty, setFaculty] = useState<ComboboxItem | null>(null);
  const facultyOptions = faculties.map(f => f.faculty).map(f => ({ value: String(f.id), label: f.name }));

  const selectedFaculty = faculties.filter(f => String(f.faculty.id) === faculty?.value)[0];

  return (
    <Stack>
      <Group>
        <Title order={1}>
          {t('annualReport.centralTitle')} {year}
        </Title>
      </Group>
      {currentRole === 'UNIVERSITY_ADMIN' && (
        <Group>
          <AnnualReportButton
            year={year}
            userId={null}
            validationStatus="VALIDATED"
          />
          <ModelReportButton
            year={year}
            userId={null}
            validationStatus="VALIDATED"
          />
        </Group>
      )}
      <Group align="flex-end">
        <NumberInput
          value={yearStr}
          onChange={setYearStr}
          label={t('annualReport.year')}
          placeholder={t('annualReport.year')}
        />
        <CustomSelect
          label="Status validare"
          description="lăsați liber pentru a vedea toată situația"
          placeholder="tot"
          searchable
          data={validationOptions}
          value={validationStatus}
          onChange={status => setValidationStatus(status as ValidationStatus)}
          clearable
        />
        <CustomSelect
          flex={1}
          label="Facultate"
          placeholder="Facultate"
          searchable
          data={facultyOptions}
          value={faculty ? faculty.value : null}
          onChange={(_value, option) => setFaculty(option)}
        />
      </Group>
      <Stack>
        {selectedFaculty == null && (
          <NoContentPlaceholder title="Vă rugăm să selectați facultatea pentru care se dorește validarea" />
        )}
        {selectedFaculty != null && (
          <Stack>
            <ReportCountBox overview={selectedFaculty.count} />
            <SimpleGrid cols={2}>
              <IsiOverviewBox
                title="Articole ISI"
                overview={selectedFaculty.articleIsiOverview}
              />
              <IsiOverviewBox
                title="Abstracte ISI"
                overview={selectedFaculty.abstractIsiOverview}
              />
            </SimpleGrid>
          </Stack>
        )}
        {/*{selectedFaculty != null && (*/}
        {/*  <AnnualFacultyReportTabs year={year} faculty={selectedFaculty} />*/}
        {/*)}*/}
      </Stack>
    </Stack>
  );
}
